import { useAgencyApi, useCurrentAgency, usePageNotifications } from '.'
import { LicensedAgencyViewModel } from '../api'
import {
  CustomerUser,
  User,
  CustomerAdmin,
  IdKeyedObjectShape,
} from 'contexts/base'
import { considerDataToBeStale } from 'utils'
import { useInvokeApi } from 'hooks'

interface CurrentAgencyUsers {
  agency: LicensedAgencyViewModel | null
  agencyUsersLookup: IdKeyedObjectShape<User>
  adminUsers: CustomerAdmin[]
  adminUserLookup: IdKeyedObjectShape<CustomerAdmin>
  nonAdminUsers: User[]
  licensedUsers: CustomerUser[]
  licensedUserLookup: IdKeyedObjectShape<CustomerUser>
  nonLicensedUsers: User[]
  initialize: () => Promise<void>
  loadAdminUsers: () => Promise<void>
  addAdminUser: (user: CustomerAdmin, onSuccess: () => void) => Promise<void>
  editAdminUser: (user: CustomerAdmin, onSuccess: () => void) => Promise<void>
  deleteAdminUser: (
    customerUserid: string,
    onSuccess: () => void,
  ) => Promise<void>
  loadLicensedUsers: () => Promise<void>
  addLicensedUser: (user: CustomerUser, onSuccess: () => void) => Promise<void>
  addLicensedUsers: (users: CustomerUser[], onSuccess: (addedUsers: CustomerUser[]) => void) => Promise<void>
  editLicensedUser: (user: CustomerUser, onSuccess: () => void) => Promise<void>
  deleteLicensedUser: (
    customerUserId: string,
    onSuccess: () => void,
  ) => Promise<void>
}

export const useCurrentAgencyUsers: (
  licensedAgencyId: string | undefined | null,
) => CurrentAgencyUsers = (licensedAgencyId) => {
  const { dispatch: dispatchNotification } = usePageNotifications()
  const agencyApi = useAgencyApi()
  const invokeApi = useInvokeApi()
  const {
    state: currentAgencyContext,
    dispatch: dispatchAgencyAction,
  } = useCurrentAgency(licensedAgencyId)

  const initialize = async (): Promise<void> => {
    await loadAgency()
    await loadAllUsers()
  }

  const loadAgency = async (): Promise<void> => {
    const isStale = considerDataToBeStale(
      currentAgencyContext.agency.data,
      currentAgencyContext.agency.lastApiReceiveAt,
    )
    if (!isStale) {
      return Promise.resolve()
    }

    await invokeApi(
      () => agencyApi.getAgency(),
      'An error occurred while loading the agency.',
      (result) => {
        dispatchAgencyAction({
          type: 'LoadAgency',
          payload: result,
          timestamp: Date.now(),
        })
      },
    )
  }

  const loadAllUsers = async (): Promise<void> => {
    const isStale = considerDataToBeStale(
      currentAgencyContext.agencyUserLookup.data,
      currentAgencyContext.agencyUsers.lastApiReceiveAt,
    )
    if (!isStale) {
      return Promise.resolve()
    }

    await invokeApi(
      () => agencyApi.getAllUsers(),
      'An error occurred while loading users.',
      (result) => {
        dispatchAgencyAction({
          type: 'LoadAgencyUsers',
          payload: result,
          timestamp: Date.now(),
        })
      },
    )
  }

  const loadLicensedUsers = async (): Promise<void> => {
    const isStale = considerDataToBeStale(
      currentAgencyContext.licensedUsers.data,
      currentAgencyContext.licensedUsers.lastApiReceiveAt,
    )
    if (!isStale) {
      return Promise.resolve()
    }

    await invokeApi(
      () => agencyApi.getLicensedUsers(),
      'An error occurred while loading users.',
      (result) => {
        dispatchAgencyAction({
          type: 'LoadLicensedUsers',
          payload: result,
          timestamp: Date.now(),
        })
      },
    )
  }

  const loadAdminUsers = async (): Promise<void> => {
    const isStale = considerDataToBeStale(
      currentAgencyContext.adminUsers.data,
      currentAgencyContext.adminUsers.lastApiReceiveAt,
    )
    if (!isStale) {
      return Promise.resolve()
    }

    await invokeApi(
      () => agencyApi.getAdminUsers(),
      'An error occurred while loading users.',
      (result) => {
        dispatchAgencyAction({
          type: 'LoadAdminUsers',
          payload: result,
          timestamp: Date.now(),
        })
      },
    )
  }

  const addAdminUser = async (
    user: CustomerAdmin,
    onSuccess: () => void,
  ): Promise<void> => {
    await invokeApi(
      () => agencyApi.addAdminUser(user),
      'An error occurred while adding the user.',
      (result) => {
        dispatchAgencyAction({
          type: 'AddAdminUser',
          payload: result,
        })

        dispatchNotification({
          type: 'SetSuccess',
          payload: 'Admin User added successfully.',
        })

        onSuccess()
      },
    )
  }

  const editAdminUser = async (
    user: CustomerAdmin,
    onSuccess: () => void,
  ): Promise<void> => {
    await invokeApi(
      () => agencyApi.editAdminUser(user),
      'An error occurred while saving the user.',
      (result) => {
        dispatchAgencyAction({
          type: 'EditAdminUser',
          payload: result,
        })

        dispatchNotification({
          type: 'SetSuccess',
          payload: 'Admin User updated successfully.',
        })

        onSuccess()
      },
    )
  }

  const deleteAdminUser = async (
    customerUserId: string,
    onSuccess: () => void,
  ): Promise<void> => {
    await invokeApi(
      () => agencyApi.deleteAdminUser(customerUserId),
      'An error occurred while deleting the user.',
      undefined,
      () => {
        dispatchAgencyAction({
          type: 'DeleteAdminUser',
          payload: customerUserId,
        })

        dispatchNotification({
          type: 'SetSuccess',
          payload: 'Admin User deleted successfully.',
        })

        onSuccess()
      },
    )
  }

  const addLicensedUser = async (
    user: CustomerUser,
    onSuccess: () => void,
  ): Promise<void> => {
    await invokeApi(
      () => agencyApi.addLicensedUser(user),
      'An error occurred while adding the user.',
      (result) => {
        dispatchAgencyAction({
          type: 'AddLicensedUser',
          payload: result,
        })

        onSuccess()

        // don't do an add notification here since we are special-casing that in Users.tsx
      },
    )
  }

  const addLicensedUsers = async (
    users: CustomerUser[],
    onSuccess: (addedUsers: CustomerUser[]) => void,
  ): Promise<void> => {

    var successCount = 0
    var failureCount = 0
    var addedUsers: CustomerUser[] = []

    users.forEach(async (user) => {

      await invokeApi(
        () => agencyApi.addLicensedUser(user),
        'An error occurred while adding the user.',
        (result) => {
          dispatchAgencyAction({
            type: 'AddLicensedUser',
            payload: result,
          })

          addedUsers.push(user)
          successCount++

          if (successCount + failureCount == users.length)
          {
            onSuccess(addedUsers)
          }
        },
        undefined,  //onSuccess is not needed since it is handled with onResult
        () => {
          failureCount++
        
          if (successCount > 0 && successCount + failureCount == users.length)
            onSuccess(addedUsers) //Partial success, still has value to the user
          else if (failureCount == users.length)
            dispatchNotification({
              type: 'SetError',
              payload: 'An error occurred while adding the users.',
            })
        },
        false
      )
    })
  }

  const editLicensedUser = async (
    user: CustomerUser,
    onSuccess: () => void,
  ): Promise<void> => {
    await invokeApi(
      () => agencyApi.editLicensedUser(user),
      'An error occurred while saving the user.',
      (result) => {
        dispatchAgencyAction({
          type: 'AddLicensedUser',
          payload: result,
        })

        dispatchNotification({
          type: 'SetSuccess',
          payload: 'User updated successfully.',
        })

        onSuccess()
      },
    )
  }

  const deleteLicensedUser = async (
    userId: string,
    onSuccess: () => void,
  ): Promise<void> => {
    await invokeApi(
      () => agencyApi.deleteLicensedUser(userId),
      'An error occurred while deleting the user.',
      undefined,
      () => {
        dispatchAgencyAction({
          type: 'DeleteLicensedUser',
          payload: userId,
        })

        dispatchNotification({
          type: 'SetSuccess',
          payload: 'User deleted successfully.',
        })

        onSuccess()
      },
    )
  }

  return {
    agency: currentAgencyContext.agency.data,
    agencyUsersLookup: currentAgencyContext.agencyUserLookup,
    adminUsers: currentAgencyContext.adminUsers.data ?? [],
    nonAdminUsers: currentAgencyContext.nonAdminUsers,
    adminUserLookup: currentAgencyContext.adminUserLookup,
    licensedUsers: currentAgencyContext.licensedUsers.data ?? [],
    licensedUserLookup: currentAgencyContext.licensedUserLookup,
    nonLicensedUsers: currentAgencyContext.nonLicensedUsers,
    initialize: initialize,
    loadAdminUsers: loadAdminUsers,
    addAdminUser: addAdminUser,
    editAdminUser: editAdminUser,
    deleteAdminUser: deleteAdminUser,
    loadLicensedUsers: loadLicensedUsers,
    addLicensedUser: addLicensedUser,
    addLicensedUsers: addLicensedUsers,
    editLicensedUser: editLicensedUser,
    deleteLicensedUser: deleteLicensedUser,
  }
}
